import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Stack,
  Snackbar,
  Alert,
  MenuItem,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  RestartAlt as RestartIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useApi } from '../contexts/ApiContext';
import { usePermissions } from '../hooks/usePermissions';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import GroupsIcon from '@mui/icons-material/Groups';
import TimelineIcon from '@mui/icons-material/Timeline';
import AvTimerIcon from '@mui/icons-material/AvTimer';
import DataUsageIcon from '@mui/icons-material/DataUsage';

const AGENT_STATES = ['AVAILABLE', 'IN_CALL', 'ACW', 'PAUSE'];
const formatDuration = (seconds = 0) => {
  if (!seconds || seconds < 0) return '00:00';
  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(secs).padStart(2, '0');
  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
};

const Sparkline = ({ values = [], color = '#667eea' }) => {
  if (!values || values.length < 2) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Sin historial suficiente
      </Typography>
    );
  }
  const data = values.slice(-30);
  const maxValue = Math.max(...data, 1);
  const denominator = Math.max(data.length - 1, 1);
  const points = data
    .map((value, index) => {
      const x = (index / denominator) * 100;
      const y = 40 - (value / maxValue) * 40;
      return `${x},${y}`;
    })
    .join(' ');
  return (
    <Box sx={{ mt: 1 }}>
      <svg width="100%" height="50" viewBox="0 0 100 40" preserveAspectRatio="none">
        <polyline
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          points={points}
        />
      </svg>
      <Typography variant="caption" color="text.secondary">
        Historial reciente (30s)
      </Typography>
    </Box>
  );
};

const StatCard = ({ title, value, helper, icon, footer }) => (
  <Box
    sx={{
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      p: 2,
      height: '100%',
      bgcolor: 'background.paper'
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          p: 1,
          borderRadius: '50%',
          bgcolor: 'primary.light',
          color: 'primary.main',
          display: 'inline-flex'
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="overline" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h5">{value}</Typography>
      </Box>
    </Stack>
    {helper && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {helper}
      </Typography>
    )}
    {footer}
  </Box>
);

const INITIAL_AGENT = { name: '', state: 'AVAILABLE' };
const INITIAL_CONFIG = {
  mode: 'Auto',
  factorBase: 1.2,
  factorIncrement: 0.1,
  factorMax: 30,
  analysisIntervalMin: 5,
  factorAdjustment: 0.1,
  cps: 5,
  maxConcurrent: 50,
  minFreeAgents: 0
};
const INITIAL_DATASET_CONFIG = {
  total: 1000,
  queue: 50,
  dialerror: 25,
  machine: 25
};
const SIM_MODES = [
  { value: 'Auto', label: 'Auto' },
  { value: 'Auto-Ultra', label: 'Auto-Ultra' },
  { value: 'Sobremarcacion', label: 'Sobremarcación' },
  { value: 'Agentes Virtuales', label: 'Agentes Virtuales' }
];

const SimulatorPage = () => {
  const { simulator } = useApi();
  const { canAction } = usePermissions();
  const [agents, setAgents] = useState([]);
  const [datasetConfig, setDatasetConfig] = useState(INITIAL_DATASET_CONFIG);
  const [config, setConfig] = useState(INITIAL_CONFIG);
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [hasLocalConfigChange, setHasLocalConfigChange] = useState(false);

  const applyConfigUpdate = (updater) => {
    setConfig((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      return next;
    });
    setHasLocalConfigChange(true);
  };

  const showMessage = (message, severity = 'success') =>
    setSnack({ open: true, message, severity });

  const loadStatus = async () => {
    if (!simulator?.getStatus) return;
    setLoadingStatus(true);
    try {
      const response = await simulator.getStatus();
      const data = response?.data ?? response;
      setStatus(data);
      if (data?.agents) {
        setAgents(data.agents);
      }
      if (data?.config && (!hasLocalConfigChange || data.running)) {
        setConfig((prev) => ({ ...prev, ...data.config }));
        if (!data.running) {
          setHasLocalConfigChange(false);
        }
      }
    } catch (error) {
      console.error('[SIMULATOR][LOAD STATUS]', error);
      showMessage(error?.message || 'No se pudo obtener el estado del simulador', 'error');
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setConfig((prev) => {
      if (
        (prev.mode === 'Sobremarcacion' || prev.mode === 'Agentes Virtuales') &&
        (prev.factorFixed === undefined || prev.factorFixed === null)
      ) {
        return { ...prev, factorFixed: prev.factorBase };
      }
      if ((prev.mode === 'Auto' || prev.mode === 'Auto-Ultra') && prev.factorFixed) {
        const { factorFixed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, [config.mode]);

  const handleAgentChange = (index, field, value) => {
    setAgents((prev) => {
      const clone = [...prev];
      const next = { ...clone[index], [field]: value };
      if (field === 'state') {
        next.lastChange = new Date().toISOString();
      }
      clone[index] = next;
      return clone;
    });
  };

  const handleAddAgent = async () => {
    try {
      const response = await simulator.addAgent({ ...INITIAL_AGENT });
      const newAgent = response?.data ?? response;
      setAgents((prev) => [...prev, newAgent]);
      showMessage('Agente creado');
    } catch (error) {
      console.error('[SIMULATOR][ADD AGENT]', error);
      showMessage(error?.message || 'No se pudo crear el agente', 'error');
    }
  };

  const handleUpdateAgent = async (agent) => {
    try {
      await simulator.updateAgent(agent.id, agent.state);
      showMessage('Estado del agente actualizado');
    } catch (error) {
      console.error('[SIMULATOR][UPDATE AGENT]', error);
      showMessage(error?.message || 'No se pudo actualizar el agente', 'error');
    }
  };

  const handleDeleteAgent = async (id) => {
    try {
      await simulator.deleteAgent(id);
      setAgents((prev) => prev.filter((agent) => agent.id !== id));
      showMessage('Agente eliminado');
    } catch (error) {
      console.error('[SIMULATOR][DELETE AGENT]', error);
      showMessage(error?.message || 'No se pudo eliminar el agente', 'error');
    }
  };

  const handleDatasetConfigChange = (field, value) => {
    setDatasetConfig((prev) => ({ ...prev, [field]: value }));
  };

  const buildDataset = () => {
    const { total, queue, dialerror, machine } = datasetConfig;
    const totalNum = parseInt(total, 10) || 0;
    const qPercent = parseFloat(queue) || 0;
    const dPercent = parseFloat(dialerror) || 0;
    const mPercent = parseFloat(machine) || 0;
    if (totalNum <= 0) throw new Error('El total debe ser mayor a cero');
    if (qPercent + dPercent + mPercent !== 100) {
      throw new Error('La suma de porcentajes debe ser 100');
    }
    const records = [];
    const counts = {
      QUEUE: Math.round((qPercent / 100) * totalNum),
      DIALERROR: Math.round((dPercent / 100) * totalNum),
      MACHINE: Math.round((mPercent / 100) * totalNum)
    };
    let remaining = totalNum - counts.QUEUE - counts.DIALERROR - counts.MACHINE;
    while (remaining > 0) {
      counts.QUEUE++;
      remaining--;
    }
    Object.entries(counts).forEach(([outcome, count]) => {
      for (let i = 0; i < count; i++) {
        const phone = `3${Math.floor(100000000 + Math.random() * 900000000)}`;
        records.push({ phone, outcome });
      }
    });
    for (let i = records.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [records[i], records[j]] = [records[j], records[i]];
    }
    return records;
  };

  const handleUploadDataset = async () => {
    try {
      const records = buildDataset();
      await simulator.uploadDataset(records);
      showMessage(`Dataset generado (${records.length} registros)`);
      loadStatus();
    } catch (error) {
      console.error('[SIMULATOR][UPLOAD DATASET]', error);
      showMessage(error?.message || 'No se pudo cargar el dataset', 'error');
    }
  };

  const handleStart = async () => {
    try {
      await simulator.start(config);
      setHasLocalConfigChange(false);
      showMessage('Simulación iniciada');
      loadStatus();
    } catch (error) {
      console.error('[SIMULATOR][START]', error);
      showMessage(error?.message || 'No se pudo iniciar la simulación', 'error');
    }
  };

  const handleStop = async () => {
    try {
      await simulator.stop();
      showMessage('Simulación detenida');
      loadStatus();
    } catch (error) {
      console.error('[SIMULATOR][STOP]', error);
      showMessage(error?.message || 'No se pudo detener la simulación', 'error');
    }
  };

  const handleReset = async () => {
    try {
      await simulator.reset();
      setDatasetConfig(INITIAL_DATASET_CONFIG);
      setHasLocalConfigChange(false);
      showMessage('Simulador reiniciado');
      loadStatus();
    } catch (error) {
      console.error('[SIMULATOR][RESET]', error);
      showMessage(error?.message || 'No se pudo reiniciar la simulación', 'error');
    }
  };

  const renderAgentsTable = () => {
    const now = Date.now();
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Nombre</TableCell>
            <TableCell>Estado</TableCell>
            <TableCell>Disponible desde</TableCell>
            <TableCell width={120}>Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {agents.map((agent, idx) => {
            const availableSinceSeconds =
              agent.state === 'AVAILABLE' && agent.lastChange
                ? Math.max(0, Math.floor((now - new Date(agent.lastChange).getTime()) / 1000))
                : null;
            return (
              <TableRow key={agent.id || idx}>
                <TableCell>
                  <TextField
                    size="small"
                    value={agent.name || ''}
                    onChange={(e) => handleAgentChange(idx, 'name', e.target.value)}
                    onBlur={() => handleUpdateAgent(agent)}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={agent.state || 'AVAILABLE'}
                    onChange={(e) => handleAgentChange(idx, 'state', e.target.value)}
                    onBlur={() => handleUpdateAgent(agent)}
                  >
                    {AGENT_STATES.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell>
                  {availableSinceSeconds !== null ? formatDuration(availableSinceSeconds) : '-'}
                </TableCell>
                <TableCell>
                  <IconButton color="secondary" onClick={() => handleDeleteAgent(agent.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };


  const renderStats = () => {
    if (!status) {
      return (
        <Typography color="text.secondary">Aun no hay lecturas del simulador.</Typography>
      );
    }
    const stats = status.stats || {};
    const cfg = status.config || config;
    const counts =
      status.agentCounts ||
      stats.agentCounts ||
      status.agents?.reduce(
        (acc, agent) => {
          switch (agent.state) {
            case 'IN_CALL':
              acc.inCall += 1;
              break;
            case 'ACW':
              acc.acw += 1;
              break;
            case 'PAUSE':
              acc.pause += 1;
              break;
            default:
              acc.available += 1;
          }
          return acc;
        },
        { available: 0, inCall: 0, acw: 0, pause: 0 }
      ) || { available: 0, inCall: 0, acw: 0, pause: 0 };
    const totalAgents = counts.available + counts.inCall + counts.acw + counts.pause;
    const availabilityPercent =
      totalAgents > 0 ? Math.round((counts.available / totalAgents) * 100) : 0;
    const availabilityInfo = status.availability || stats.availability || {
      averageSeconds: 0,
      maxSeconds: 0
    };
    const datasetTotal = stats.datasetSize ?? status.dataset?.total ?? 0;
    const datasetPending = stats.datasetPending ?? status.dataset?.pending ?? 0;
    const datasetProcessed = Math.max(datasetTotal - datasetPending, 0);
    const datasetPercent =
      datasetTotal > 0 ? Math.round((datasetProcessed / datasetTotal) * 100) : 0;
    const activePercent =
      cfg?.maxConcurrent > 0
        ? Math.min(100, Math.round(((stats.inProgress || 0) / cfg.maxConcurrent) * 100))
        : 0;
    const history = status.history || [];
    const sparklineCalls = history.map((point) => point.inProgress ?? 0);
    const sparklineFactor = history.map((point) => point.factor ?? stats.factor ?? 0);

    const statCards = [
      {
        title: 'Estado del motor',
        value: status.running ? 'En ejecucion' : 'Detenido',
        helper: `Modo ${cfg?.mode || 'Auto'}`,
        icon: <SmartToyIcon fontSize="small" />,
        footer: (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Actualizado {new Date(stats.timestamp || Date.now()).toLocaleTimeString()}
          </Typography>
        )
      },
      {
        title: 'Factor',
        value: (stats.factor ?? 0).toFixed(2),
        helper:
          cfg?.mode === 'Sobremarcacion'
            ? `Llamadas/agente: ${cfg?.factorFixed ?? cfg?.factorBase ?? 0}`
            : `Incremento: ${cfg?.factorIncrement ?? 0}`,
        icon: <TimelineIcon fontSize="small" />,
        footer: <Sparkline values={sparklineFactor} color="#ff9800" />
      },
      {
        title: 'Llamadas activas',
        value: stats.inProgress ?? 0,
        helper: `${stats.callsConnected ?? 0} conectadas / ${stats.abandoned ?? 0} abandonos`,
        icon: <PhoneInTalkIcon fontSize="small" />,
        footer: (
          <Box sx={{ mt: 1 }}>
            <LinearProgress
              variant="determinate"
              value={activePercent}
              sx={{ height: 8, borderRadius: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              Objetivo {cfg?.maxConcurrent ?? 0} simultaneas
            </Typography>
          </Box>
        )
      },
      {
        title: 'Dataset procesado',
        value: `${datasetPercent}%`,
        helper: `${datasetProcessed}/${datasetTotal} registros`,
        icon: <DataUsageIcon fontSize="small" />,
        footer: (
          <Box sx={{ mt: 1 }}>
            <LinearProgress
              variant="determinate"
              value={datasetPercent}
              color="secondary"
              sx={{ height: 8, borderRadius: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              Pendientes: {datasetPending}
            </Typography>
          </Box>
        )
      },
      {
        title: 'Agentes',
        value: `${counts.available} disponibles`,
        helper: `Llamada: ${counts.inCall} | ACW: ${counts.acw} | Pausa: ${counts.pause}`,
        icon: <GroupsIcon fontSize="small" />,
        footer: (
          <Box sx={{ mt: 1 }}>
            <LinearProgress
              variant="determinate"
              value={availabilityPercent}
              color="success"
              sx={{ height: 8, borderRadius: 1 }}
            />
            <Typography variant="caption" color="text.secondary">
              Disponibilidad {availabilityPercent}%
            </Typography>
          </Box>
        )
      },
      {
        title: 'Tiempo disponible',
        value: formatDuration(availabilityInfo.averageSeconds),
        helper: 'Promedio por agente disponible',
        icon: <AvTimerIcon fontSize="small" />,
        footer: (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Maximo {formatDuration(availabilityInfo.maxSeconds)}
          </Typography>
        )
      }
    ];

    return (
      <Grid container spacing={2}>
        {statCards.map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.title}>
            <StatCard {...card} />
          </Grid>
        ))}
        <Grid item xs={12}>
          <Box sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Historial de llamadas en curso
            </Typography>
            <Typography variant="h6">
              {stats.inProgress ?? 0} llamadas ahora / {cfg?.cps ?? 0} cps
            </Typography>
            <Sparkline values={sparklineCalls} color="#667eea" />
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderModeSelectField = () => (
    <Grid item xs={12} md={3}>
      <TextField
        select
        label="Modo"
        value={config.mode}
        onChange={(e) => applyConfigUpdate({ mode: e.target.value })}
        fullWidth
      >
        {SIM_MODES.map((mode) => (
          <MenuItem key={mode.value} value={mode.value}>
            {mode.label}
          </MenuItem>
        ))}
      </TextField>
    </Grid>
  );

  const renderCommonLimits = (includeMinFree = true) => (
    <>
      <Grid item xs={12} md={3}>
        <TextField
          type="number"
          label="CPS"
          value={config.cps}
          onChange={(e) =>
            applyConfigUpdate((prev) => ({ ...prev, cps: parseInt(e.target.value, 10) || 0 }))
          }
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          type="number"
          label="Max llamadas simultáneas"
          value={config.maxConcurrent}
          onChange={(e) =>
            applyConfigUpdate((prev) => ({
              ...prev,
              maxConcurrent: parseInt(e.target.value, 10) || 0
            }))
          }
          fullWidth
        />
      </Grid>
      {includeMinFree && (
        <Grid item xs={12} md={3}>
          <TextField
            type="number"
            label="Agentes libres reservados"
            value={config.minFreeAgents}
            onChange={(e) =>
              applyConfigUpdate((prev) => ({
                ...prev,
                minFreeAgents: parseInt(e.target.value, 10) || 0
              }))
            }
            fullWidth
          />
        </Grid>
      )}
    </>
  );

  const renderConfigFields = () => {
    if (config.mode === 'Sobremarcacion') {
      return (
        <Grid container spacing={2}>
          {renderModeSelectField()}
          <Grid item xs={12} md={3}>
            <TextField
              type="number"
              label="Llamadas por agente"
              value={config.factorFixed ?? config.factorBase}
              onChange={(e) =>
                applyConfigUpdate((prev) => ({
                  ...prev,
                  factorFixed: parseFloat(e.target.value) || 0
                }))
              }
              fullWidth
            />
          </Grid>
          {renderCommonLimits(true)}
        </Grid>
      );
    }

    if (config.mode === 'Agentes Virtuales') {
      return (
        <Grid container spacing={2}>
          {renderModeSelectField()}
          <Grid item xs={12} md={3}>
            <TextField
              type="number"
              label="Sesiones objetivo"
              value={config.factorFixed ?? config.factorBase}
              onChange={(e) =>
                applyConfigUpdate((prev) => ({
                  ...prev,
                  factorFixed: parseFloat(e.target.value) || 0
                }))
              }
              fullWidth
            />
          </Grid>
          {renderCommonLimits(false)}
        </Grid>
      );
    }

    return (
      <Grid container spacing={2}>
        {renderModeSelectField()}
        <Grid item xs={12} md={3}>
          <TextField
            type="number"
            label="Factor base"
            value={config.factorBase}
            onChange={(e) =>
              applyConfigUpdate((prev) => ({
                ...prev,
                factorBase: parseFloat(e.target.value)
              }))
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            type="number"
            label="Incremento factor"
            value={config.factorIncrement}
            onChange={(e) =>
              applyConfigUpdate((prev) => ({
                ...prev,
                factorIncrement: parseFloat(e.target.value)
              }))
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            type="number"
            label="Tiempo de análisis (min)"
            value={config.analysisIntervalMin}
            onChange={(e) =>
              applyConfigUpdate((prev) => ({
                ...prev,
                analysisIntervalMin: parseInt(e.target.value) || 5
              }))
            }
            fullWidth
            helperText="Intervalo para ajustar factor por abandonos"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            type="number"
            label="Ajuste automático (±)"
            value={config.factorAdjustment}
            onChange={(e) =>
              applyConfigUpdate((prev) => ({
                ...prev,
                factorAdjustment: parseFloat(e.target.value) || 0.1
              }))
            }
            fullWidth
            helperText="Incremento/decremento por abandonos"
          />
        </Grid>
        {renderCommonLimits(true)}
      </Grid>
    );
  };
  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Stack direction="row" spacing={1} alignItems="center">
        <SmartToyIcon color="primary" fontSize="large" />
        <Typography variant="h4">Simulador del Motor Predictivo</Typography>
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Card>
            <CardHeader
              title="Agentes Simulados"
              action={
                <Button startIcon={<AddIcon />} onClick={handleAddAgent}>
                  Nuevo agente
                </Button>
              }
            />
            <CardContent>{renderAgentsTable()}</CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Card>
            <CardHeader
              title="Dataset Simulado"
              action={
                <Button variant="contained" onClick={handleUploadDataset}>
                  Generar dataset
                </Button>
              }
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="number"
                    label="Total registros"
                    value={datasetConfig.total}
                    onChange={(e) => handleDatasetConfigChange('total', parseInt(e.target.value, 10) || 0)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="number"
                    label="% Queue"
                    value={datasetConfig.queue}
                    onChange={(e) => handleDatasetConfigChange('queue', parseFloat(e.target.value) || 0)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="number"
                    label="% DialError"
                    value={datasetConfig.dialerror}
                    onChange={(e) => handleDatasetConfigChange('dialerror', parseFloat(e.target.value) || 0)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="number"
                    label="% Machine"
                    value={datasetConfig.machine}
                    onChange={(e) => handleDatasetConfigChange('machine', parseFloat(e.target.value) || 0)}
                    fullWidth
                  />
                </Grid>
              </Grid>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Los porcentajes deben sumar 100. Se generarán números aleatorios con resultados distribuidos
                según los porcentajes definidos.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardHeader title="Configuración del Motor" />
        <CardContent sx={{ pointerEvents: canAction('config_simulator') ? 'auto' : 'none', opacity: canAction('config_simulator') ? 1 : 0.5 }}>
          {renderConfigFields()}
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={2}>
            {canAction('run_simulator') && (
              !status?.running ? (
                <Button variant="contained" startIcon={<PlayIcon />} onClick={handleStart}>
                  Iniciar
                </Button>
              ) : (
                <Button variant="outlined" startIcon={<StopIcon />} onClick={handleStop}>
                  Detener
                </Button>
              )
            )}
            {canAction('run_simulator') && (
              <Button startIcon={<RestartIcon />} onClick={handleReset}>
                Reiniciar
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title="Métricas en tiempo real"
          action={loadingStatus ? <CircularProgress size={16} /> : null}
        />
        <CardContent>{renderStats()}</CardContent>
      </Card>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          severity={snack.severity}
          variant="filled"
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SimulatorPage;













